import { z } from "zod";

import { Address, Amount, Asset, Fiat, OnrampMethod, RampName, Token, TokenPointer } from "@shared/types";
import { UUID_REGEX } from "@shared/constants";

export const PaymentId = z.string().refine((id) => UUID_REGEX.test(id), {
    message: "Invalid payment ID",
});
export type PaymentId = z.infer<typeof PaymentId>;

export const Feature = z.enum(["BETA_EDGES", "ORG_BETA_EDGES", "ORG_EDGES"]);
export type Feature = z.infer<typeof Feature>;

const BaseAssetFilters = z.object({
    onramps: z.array(RampName).optional(),
    offramps: z.array(RampName).optional(),
    sandbox: z.boolean().optional().default(false),
    features: z.array(Feature).optional(),
});

export const InputsAssetFilters = BaseAssetFilters.extend({
    inputs: z.array(Asset).optional(),
    outputs: z.array(Asset),
});
export type InputsAssetFilters = z.input<typeof InputsAssetFilters>;

export const OutputsAssetFilters = BaseAssetFilters.extend({
    inputs: z.array(Asset),
    outputs: z.array(Asset).optional(),
});
export type OutputsAssetFilters = z.input<typeof OutputsAssetFilters>;

export const AssetFilters = z.union([InputsAssetFilters, OutputsAssetFilters]);
export type AssetFilters = z.infer<typeof AssetFilters>;

const AssetAmount = z.object({
    asset: Asset,
    amount: Amount,
});
type AssetAmount = z.infer<typeof AssetAmount>;

const TokenAmount = z.object({
    token: Token,
    amount: Amount,
});
type TokenAmount = z.infer<typeof TokenAmount>;

const Signature = z.string();
type Signature = z.infer<typeof Signature>;

export const IPAddressOverride = z.union([z.ipv4(), z.ipv6()]);

const BaseQuoteRequest = z.object({
    price_currency: Fiat,

    // onramp filters
    onramps: z.array(RampName).optional(),
    onramp_methods: z.array(OnrampMethod).optional(),

    // XXX add offramp filters

    // optional customer information
    customer_ip_address: z.string().optional(),
    customer_id: z.string().optional(),

    // optional label for the payment
    label: z.string().optional(),

    // optional parent payment ID for re-quote
    parent_payment_id: PaymentId.optional(),

    features: z.array(Feature).optional(),

    // optional rev share name
    rev_share_name: z.string().optional(),

    // optional safety overrides
    allow_unsafe_transfer_out: z.boolean().optional(),
    show_all_issues: z.boolean().optional(),
    allow_exceed_max_input_limit: z.boolean().optional(),
    allow_unsafe_slippage: z.boolean().optional(),
});

const BigIntString = z.object({
    "#": z.string(),
});

const CallImmediateAction = z.object({
    type: z.literal("CallImmediateAction"),
    target: z.string(),
    value: BigIntString,
    calldata: z.string(),
});

const CustomActions = z.object({
    actions: z.array(CallImmediateAction),
    user_estimated_gas: BigIntString,
});

const FixedInput = z.object({
    kind: z.literal("FIXED_INPUT"),
    fixed_input_amount: AssetAmount,
    output_asset: Asset,
    custom_actions: CustomActions.optional(),
    dest_address: z.string().optional(),
});
type FixedInput = z.infer<typeof FixedInput>;

// TODO: uncomment this when we support FIXED_OUTPUT quotes
// const FixedOutput = z.object({
//     kind: z.literal("FIXED_OUTPUT"),
//     input_asset: Asset,
//     fixed_output_amount: AssetAmount,
// });
// type FixedOutput = z.infer<typeof FixedOutput>;

const FixedRequest = z.discriminatedUnion("kind", [FixedInput]);

export const RequestForQuotes = BaseQuoteRequest.extend({
    request: FixedRequest,
});
export type RequestForQuotes = z.infer<typeof RequestForQuotes>;

// Status
export const Account = z.enum(["USER", "DEST", "HALLIDAY", "SPW", "REV_SHARE", "BRIDGE"]);
export type Account = z.infer<typeof Account>;

const Property = z.enum(["APPROVAL", "BALANCE"]);
type Property = z.infer<typeof Property>;
const Resource = z.object({
    asset: Asset,
    property: Property,
});
type Resource = z.infer<typeof Resource>;

const ChangeStructure = z.object({
    account: Account,
    resource: Resource,
});
type ChangeStructure = z.infer<typeof ChangeStructure>;

const TerminalObservedStatus = z.union([z.literal("COMPLETE"), z.literal("UNREACHABLE"), z.literal("FAILED")]);
type TerminalObservedStatus = z.infer<typeof TerminalObservedStatus>;
const NonTerminalObservedStatus = z.literal("PENDING");
type NonTerminalObservedStatus = z.infer<typeof NonTerminalObservedStatus>;
export const ObservedStatus = z.union([TerminalObservedStatus, NonTerminalObservedStatus]);
export type ObservedStatus = z.infer<typeof ObservedStatus>;

const WorkflowStatus = z.enum(["PENDING", "COMPLETE", "FAILED", "EXPIRED", "WITHDRAWN", "TAINTED"]);
type WorkflowStatus = z.infer<typeof WorkflowStatus>;

const EffectStructure = z.object({
    consume: z.array(ChangeStructure),
    produce: z.array(ChangeStructure),
});
type EffectStructure = z.infer<typeof EffectStructure>;

export const ChangeAmount = ChangeStructure.extend({
    amount: Amount,
});
export type ChangeAmount = z.infer<typeof ChangeAmount>;

const EffectAmount = EffectStructure.extend({
    consume: z.array(ChangeAmount),
    produce: z.array(ChangeAmount),
});
type EffectAmount = z.infer<typeof EffectAmount>;

export const Prices = z.record(Asset, z.string());
export type Prices = z.infer<typeof Prices>;

const AmountIssue = z.object({
    kind: z.union([z.literal("amount"), z.literal("amount-downstream")]),
    given: z.string(),
    limits: z.object({
        min: z.string().optional(),
        max: z.string().optional(),
    }),
    source: z.string(),
    message: z.string(),
});
type AmountIssue = z.infer<typeof AmountIssue>;

const GeoLocationIssue = z.object({
    kind: z.literal("geolocation"),
    message: z.string(),
});
type GeoLocationIssue = z.infer<typeof GeoLocationIssue>;

const ProviderIssue = z.object({
    kind: z.literal("provider"),
    message: z.string(),
});
type ProviderIssue = z.infer<typeof ProviderIssue>;

const OtherIssue = z.object({
    kind: z.literal("other"),
    message: z.string(),
});
type OtherIssue = z.infer<typeof OtherIssue>;

const UnknownIssue = z.object({
    kind: z.literal("unknown"),
    message: z.string(),
});
type UnknownIssue = z.infer<typeof UnknownIssue>;

export const Issue = z.discriminatedUnion("kind", [
    AmountIssue,
    GeoLocationIssue,
    ProviderIssue,
    OtherIssue,
    UnknownIssue,
]);
export type Issue = z.infer<typeof Issue>;

export const Failure = z.object({
    service_ids: z.array(z.string()),
    latency_seconds: z.number(),
    issues: z.array(Issue).optional(),
});
export type Failure = z.infer<typeof Failure>;

export const RouteType = z.union([z.literal("USER_FUND"), z.literal("ONCHAIN_STEP"), z.literal("ONRAMP")]);
export type RouteType = z.infer<typeof RouteType>;

export const QuotedRoute = z.object({
    type: RouteType,
    net_effect: EffectAmount,
    pieces_info: z.array(
        z.object({
            type: z.union([
                z.literal("onramp"),
                z.literal("poll"),
                z.literal("bridge"),
                z.literal("rev_share"),
                z.literal("transfer_out"),
                z.literal("convert"),
            ]),
        })
    ),
    step_index: z.number().optional(),
});
export type QuotedRoute = z.infer<typeof QuotedRoute>;

export const Quote = z.object({
    payment_id: z.string(),
    output_amount: z.object({
        asset: Asset,
        amount: z.string(),
    }),
    onramp: RampName.optional(),
    onramp_method: OnrampMethod.optional(),
    fees: z.object({
        total_fees: z.string(),
        conversion_fees: z.string(),
        network_fees: z.string(),
        business_fees: z.string(),
        currency_symbol: Fiat,
    }),
    route: z.array(QuotedRoute),
});
export type Quote = z.infer<typeof Quote>;

export const FulfillmentRoute = QuotedRoute.extend({
    status: ObservedStatus,
    transaction_hash: z.string().optional(),
});
export type FulfillmentRoute = z.infer<typeof FulfillmentRoute>;

export const Fulfillment = Quote.omit({
    payment_id: true,
})
    .partial({
        output_amount: true,
        onramp: true,
        onramp_method: true,
        fees: true,
    })
    .extend({
        route: z.array(FulfillmentRoute),
    });

export type Fulfillment = z.infer<typeof Fulfillment>;

export const QuotesResponse = z.object({
    quote_request: RequestForQuotes,
    quotes: z.array(Quote),
    current_prices: Prices,
    price_currency: Fiat,
    state_token: z.string(),
    quoted_at: z.string(),
    accept_by: z.string(),
    failures: z.array(Failure),
});
export type QuotesResponse = z.infer<typeof QuotesResponse>;

export const RequestForConfirmQuote = z.object({
    payment_id: PaymentId,
    state_token: z.string(),
    owner_chain: z.string().optional(),
    owner_address: Address,
    destination_address: Address,
    client_redirect_url: z.string().optional(),
    allow_unsafe_owner: z.boolean().optional(),
});
export type RequestForConfirmQuote = z.infer<typeof RequestForConfirmQuote>;

export const DepositInfo = z.object({
    deposit_token: Token,
    deposit_chain: z.string(),
    deposit_amount: Amount,
    deposit_address: Address,
});
export type DepositInfo = z.infer<typeof DepositInfo>;

export const NextInstruction = z.object({
    type: z.enum(["ONRAMP", "TRANSFER_IN"]),
    payment_id: PaymentId,
    funding_page_url: z.url(),
    deposit_info: z.array(DepositInfo),
});
export type NextInstruction = z.infer<typeof NextInstruction>;

export const ConfirmQuoteResponse = z.object({
    payment_id: PaymentId,
    org_id: z.string(),
    status: WorkflowStatus,
    funded: z.boolean(),
    created_at: z.string(),
    updated_at: z.string(),
    initiate_fund_by: z.string(),
    completed_at: z.string().optional(),
    quote_request: RequestForQuotes,
    quoted: Quote.omit({
        payment_id: true,
    }),
    fulfilled: Fulfillment,
    current_prices: Prices,
    price_currency: Fiat,
    customer_id: z.string().optional(),
    label: z.string().optional(),
    processing_addresses: z.array(
        z.object({
            address: Address,
            chain: z.string(),
        })
    ),
    owner_chain: z.string(),
    owner_address: Address,
    destination_address: Address,
    next_instruction: NextInstruction.optional(),
    parent_payment_id: PaymentId.optional(),
    client_redirect_url: z.string().nullable(),
});
export type ConfirmQuoteResponse = z.infer<typeof ConfirmQuoteResponse>;

export const PaymentStatus = ConfirmQuoteResponse;
export type PaymentStatus = z.infer<typeof PaymentStatus>;

export const RequestForOnrampSession = z.object({
    payment_id: PaymentId,
    redirect_url: z.string(),
    referrer_domain: z.string().optional(),
});
export type RequestForOnrampSession = z.infer<typeof RequestForOnrampSession>;

export const OnrampSessionResponse = z.object({
    funding_page_onramp_url: z.string(),
    client_redirect_url: z.string().optional(),
    client_secret: z.string().optional(),
});
export type OnrampSessionResponse = z.infer<typeof OnrampSessionResponse>;

export const RequestForPaymentsHistory = z.object({
    id_query: z.string().optional(),
    pagination_key: z.string().optional(),
    dest_address: Address.optional(),
    owner_address: Address.optional(),
    limit: z.number().int().positive().optional(),
    category: z.enum(["ALL", "NEW_OR_FUNDED"]).optional(),
    label: z.string().optional(),
});
export type RequestForPaymentsHistory = z.infer<typeof RequestForPaymentsHistory>;

export const PaymentHistoryResult = z.object({
    payment_statuses: z.array(PaymentStatus),
    next_pagination_key: z.string().nullable(),
});
export type PaymentHistoryResult = z.infer<typeof PaymentHistoryResult>;

export const RequestForBalance = z
    .object({
        payment_id: PaymentId.optional(),
        custom_queries: z
            .array(
                z.object({
                    address: Address,
                    token: z.union([Token, TokenPointer]),
                })
            )
            .optional(),
    })
    .refine(
        (data) => {
            if (!data.payment_id && !data.custom_queries?.length) {
                return false;
            }
            return true;
        },
        {
            message: "payment_id or custom_queries is required",
        }
    );
export type RequestForBalance = z.infer<typeof RequestForBalance>;

export const BalanceResponse = z.object({
    balance_results: z.array(
        z.object({
            address: Address,
            token: Token,
            value: z.discriminatedUnion("kind", [
                z.object({
                    kind: z.literal("amount"),
                    amount: Amount,
                }),
                z.object({
                    kind: z.literal("error"),
                }),
            ]),
        })
    ),
});
export type BalanceResponse = z.infer<typeof BalanceResponse>;

export const RequestForPrices = z.object({
    fiat: Fiat,
    tokens: z.array(Token),
});
export type RequestForPrices = z.infer<typeof RequestForPrices>;

export const TypedData = z.string();
export type TypedData = z.infer<typeof TypedData>;

export const RequestForWithdrawAuthorization = z.object({
    payment_id: PaymentId,
    token_amounts: z.array(TokenAmount),
    recipient_address: Address,
});
export type RequestForWithdrawAuthorization = z.infer<typeof RequestForWithdrawAuthorization>;

export const WithdrawAuthorizationResponse = z.object({
    payment_id: PaymentId,
    withdraw_authorization: TypedData,
});
export type WithdrawAuthorizationResponse = z.infer<typeof WithdrawAuthorizationResponse>;

export const RequestForWithdraw = RequestForWithdrawAuthorization.extend({
    owner_signature: Signature,
});
export type RequestForWithdraw = z.infer<typeof RequestForWithdraw>;

export const WithdrawResponse = z.object({
    payment_id: PaymentId,
    transaction_hash: z.string(),
});
export type WithdrawResponse = z.infer<typeof WithdrawResponse>;
